import React, { useState } from "react";
import {
  Button,
  Card,
  Typography,
  Radio,
  Spin,
  message,
  Progress,
  ConfigProvider,
  Row,
  Col,
} from "antd";
import {
  PlayCircleFilled,
  TrophyFilled,
  SmileFilled,
  RocketFilled,
  HomeFilled,
  StarFilled,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import type { RadioChangeEvent } from "antd";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import "./App.css"; // Đảm bảo import CSS mới

const { Title, Text } = Typography;

// --- Interfaces ---
interface Question {
  question: string;
  options: string[];
  correctAnswer: string;
  explanation?: string;
}

interface ApiResponse {
  result: string;
  used_model: string;
}

// --- Cấu hình chủ đề (Màu sắc Pastel) ---
const TOPICS = [
  {
    key: "animals",
    label: "Thế giới động vật",
    icon: <SmileFilled style={{ fontSize: 32 }} />,
    color: "#FFAB91", // Cam nhạt
    shadow: "#D84315",
  },
  {
    key: "science",
    label: "Khoa học vui",
    icon: <RocketFilled style={{ fontSize: 32 }} />,
    color: "#81D4FA", // Xanh dương nhạt
    shadow: "#0277BD",
  },
  {
    key: "math",
    label: "Toán học",
    icon: <PlayCircleFilled style={{ fontSize: 32 }} />,
    color: "#A5D6A7", // Xanh lá nhạt
    shadow: "#2E7D32",
  },
  {
    key: "fairy_tales",
    label: "Truyện cổ tích",
    icon: <TrophyFilled style={{ fontSize: 32 }} />,
    color: "#CE93D8", // Tím nhạt
    shadow: "#7B1FA2",
  },
];

const App: React.FC = () => {
  const [gameState, setGameState] = useState<
    "menu" | "loading" | "playing" | "finished"
  >("menu");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);

  // --- API Call ---
  const fetchQuestions = async (topicLabel: string) => {
    setGameState("loading");
    setScore(0);
    setCurrentQIndex(0);

    const promptText = `
      Đóng vai người bạn thân thiết của trẻ nhỏ. Tạo 5 câu hỏi đố vui về chủ đề "${topicLabel}".
      Ngôn ngữ: Tiếng Việt.
      Độ khó: Dễ, vui nhộn (6-10 tuổi).
      Định dạng: JSON Array.
      Yêu cầu:
      1. Câu hỏi ngắn gọn, dùng từ ngữ thân thiện (Ví dụ: "Đố bé biết...", "Bạn nào...").
      2. Giải thích (explanation) phải mang tính khích lệ, dùng icon vui (🌟, 🎉).
      3. Có thể dùng Markdown (in đậm **...**) để nhấn mạnh từ khóa.

      Cấu trúc JSON bắt buộc:
      [
        {
          "question": "Nội dung câu hỏi (có thể chứa markdown)?",
          "options": ["Đáp án A", "Đáp án B", "Đáp án C", "Đáp án D"],
          "correctAnswer": "Đáp án đúng (khớp y hệt options)",
          "explanation": "Giải thích kiến thức ngắn gọn (Không chứa lời chúc mừng)."
        }
      ]
    `;

    try {
      const response = await axios.post<ApiResponse>(
        "https://groqprompt.netlify.app/api/ai",
        { prompt: promptText }
      );

      let cleanJson = response.data.result
        .replace(/```json/g, "")
        .replace(/```/g, "")
        .trim();

      const firstBracket = cleanJson.indexOf("[");
      const lastBracket = cleanJson.lastIndexOf("]");
      if (firstBracket !== -1 && lastBracket !== -1) {
        cleanJson = cleanJson.substring(firstBracket, lastBracket + 1);
      }

      const parsedQuestions: Question[] = JSON.parse(cleanJson);
      if (parsedQuestions.length > 0) {
        setQuestions(parsedQuestions);
        setGameState("playing");
      } else {
        message.error("Ôi, AI đang ngủ gật rồi. Bé thử lại nhé!");
        setGameState("menu");
      }
    } catch (error) {
      console.error(error);
      message.error("Có chút trục trặc mạng, bé kiểm tra lại wifi nha!");
      setGameState("menu");
    }
  };

  const handleAnswerSelect = (e: RadioChangeEvent) =>
    setSelectedOption(e.target.value);

  const checkAnswer = () => {
    if (!selectedOption) return;
    setShowResult(true);
    if (selectedOption === questions[currentQIndex].correctAnswer) {
      setScore((prev) => prev + 1);
      // Sound effect could go here
    }
  };

  const nextQuestion = () => {
    setSelectedOption(null);
    setShowResult(false);
    if (currentQIndex < questions.length - 1) {
      setCurrentQIndex((prev) => prev + 1);
    } else {
      setGameState("finished");
    }
  };

  const restartGame = () => {
    setGameState("menu");
    setQuestions([]);
    setScore(0);
    setSelectedOption(null);
    setShowResult(false);
  };

  const MarkdownText = ({ content }: { content: string }) => (
    <div className="markdown-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          p: ({ node, ...props }) => <p {...props} />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );

  // --- Components ---

  const renderMenu = () => (
    <div className="app-container animate-pop-in">
      <Card className="cozy-card" bordered={false}>
        <Title className="game-title">🌈 ĐỐ VUI SIÊU NHÍ</Title>
        <Text
          style={{
            display: "block",
            textAlign: "center",
            fontSize: "1.2rem",
            color: "#666",
            marginBottom: 30,
          }}
        >
          Bé muốn khám phá chủ đề nào hôm nay?
        </Text>

        <Row gutter={[16, 16]} justify="center">
          {TOPICS.map((topic) => (
            <Col xs={24} sm={12} md={6} key={topic.key}>
              <Button
                className="topic-btn"
                block
                style={{
                  backgroundColor: topic.color,
                  color: "#fff",
                  height: "120px",
                }}
                onClick={() => fetchQuestions(topic.label)}
              >
                {topic.icon}
                <span>{topic.label}</span>
              </Button>
            </Col>
          ))}
        </Row>
      </Card>
    </div>
  );

  const renderLoading = () => (
    <div className="app-container" style={{ textAlign: "center" }}>
      <Spin size="large" />
      <Title level={3} style={{ color: "#FF7F50", marginTop: 20 }}>
        🤖 Đang tìm câu hỏi hay cho bé...
      </Title>
    </div>
  );

  const renderGame = () => {
    const currentQ = questions[currentQIndex];
    const progress = (currentQIndex / questions.length) * 100;

    return (
      <div className="app-container">
        <div style={{ marginBottom: 15, padding: "0 10px" }}>
          <Progress
            percent={progress}
            showInfo={false}
            strokeColor="#FF7F50"
            trailColor="#FFE0B2"
            strokeWidth={12}
            style={{ borderRadius: 10 }}
          />
        </div>

        <Card className="cozy-card animate-pop-in" bordered={false}>
          {/* Header câu hỏi */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                background: "#E0F7FA",
                padding: "5px 15px",
                borderRadius: 20,
                color: "#006064",
                fontWeight: "bold",
              }}
            >
              Câu hỏi {currentQIndex + 1}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                color: "#FBC02D",
                fontWeight: "bold",
                fontSize: "1.1rem",
              }}
            >
              <StarFilled /> {score} điểm
            </div>
          </div>

          {/* Nội dung câu hỏi */}
          <div
            style={{
              minHeight: 80,
              fontSize: "1.3rem",
              fontWeight: 700,
              color: "#333",
              marginBottom: 25,
              textAlign: "center",
            }}
          >
            <MarkdownText content={currentQ.question} />
          </div>

          {/* Danh sách đáp án */}
          <Radio.Group
            onChange={handleAnswerSelect}
            value={selectedOption}
            style={{ width: "100%" }}
            disabled={showResult}
          >
            <div className="answer-radio-group">
              {currentQ.options.map((opt, idx) => {
                let cardClass = "answer-card";
                let icon = null;

                if (selectedOption === opt) cardClass += " selected";

                if (showResult) {
                  if (opt === currentQ.correctAnswer) {
                    cardClass += " correct";
                    icon = (
                      <CheckCircleFilled
                        style={{
                          color: "#52c41a",
                          fontSize: 20,
                          marginLeft: "auto",
                        }}
                      />
                    );
                  } else if (opt === selectedOption) {
                    cardClass += " wrong";
                    icon = (
                      <CloseCircleFilled
                        style={{
                          color: "#ff4d4f",
                          fontSize: 20,
                          marginLeft: "auto",
                        }}
                      />
                    );
                  }
                }

                return (
                  <Radio
                    key={idx}
                    value={opt}
                    className={cardClass}
                    style={{ width: "100%" }}
                  >
                    <div style={{ flex: 1 }}>
                      <MarkdownText content={opt} />
                    </div>
                    {icon}
                  </Radio>
                );
              })}
            </div>
          </Radio.Group>

          {/* Phần giải thích */}
          {showResult && (
            <div
              className="animate-pop-in"
              style={{
                marginTop: 25,
                padding: 20,
                backgroundColor: "#F9F9F9",
                borderRadius: 15,
                borderLeft: "5px solid #FF7F50",
              }}
            >
              <Text strong style={{ fontSize: "1.1rem", color: "#FF7F50" }}>
                🌟 Giải thích cho bé:
              </Text>
              <div style={{ marginTop: 8, fontSize: "1rem", color: "#555" }}>
                <MarkdownText content={currentQ.explanation || ""} />
              </div>
            </div>
          )}

          {/* Nút điều hướng */}
          <div style={{ marginTop: 30, textAlign: "center" }}>
            {!showResult ? (
              <Button
                type="primary"
                className="action-btn"
                onClick={checkAnswer}
                disabled={!selectedOption}
                style={{ width: "200px", background: "#FF7F50" }}
              >
                Kiểm tra nhé!
              </Button>
            ) : (
              <Button
                type="primary"
                className="action-btn"
                onClick={nextQuestion}
                style={{ width: "200px", background: "#8BC34A" }}
              >
                {currentQIndex < questions.length - 1
                  ? "Câu tiếp theo ➜"
                  : "Xem điểm số"}
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  };

  const renderResult = () => (
    <div className="app-container animate-pop-in">
      <Card className="cozy-card" style={{ textAlign: "center", padding: 40 }}>
        {score === questions.length ? (
          <TrophyFilled
            style={{ fontSize: 80, color: "#FFD700", marginBottom: 20 }}
          />
        ) : (
          <SmileFilled
            style={{ fontSize: 80, color: "#FF7F50", marginBottom: 20 }}
          />
        )}

        <Title level={2} style={{ color: "#333", margin: 0 }}>
          {score === questions.length ? "XUẤT SẮC QUÁ!" : "BÉ LÀM TỐT LẮM!"}
        </Title>

        <Text
          style={{
            fontSize: "1.2rem",
            display: "block",
            marginTop: 10,
            marginBottom: 30,
            color: "#666",
          }}
        >
          Bé đã trả lời đúng{" "}
          <strong style={{ color: "#FF7F50", fontSize: "1.5rem" }}>
            {score}
          </strong>{" "}
          / {questions.length} câu hỏi.
        </Text>

        <Button
          type="primary"
          size="large"
          icon={<HomeFilled />}
          className="action-btn"
          onClick={restartGame}
          style={{ background: "#4DB6AC", padding: "0 40px" }}
        >
          Chơi lại nào
        </Button>
      </Card>
    </div>
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "'Nunito', sans-serif",
          colorPrimary: "#FF7F50",
          borderRadius: 16,
        },
      }}
    >
      {gameState === "menu" && renderMenu()}
      {gameState === "loading" && renderLoading()}
      {gameState === "playing" && renderGame()}
      {gameState === "finished" && renderResult()}
    </ConfigProvider>
  );
};

export default App;
